<template>
  <div class="page-container">
    <!-- 工具条 -->
    <div class="toolbar">
      <div class="left">
        <el-button round @click="fetchList" :loading="loading">
          <el-icon>
            <Refresh />
          </el-icon> 刷新
        </el-button>

        <el-input v-model="search.keyword" placeholder="按任务名/voice_id 搜索" clearable style="width: 280px"
          @keyup.enter="doSearch" class="ml10">
          <template #prefix><el-icon>
              <Search />
            </el-icon></template>
        </el-input>
        <el-button type="primary" class="ml10" @click="doSearch">
          <el-icon>
            <Search />
          </el-icon>搜索
        </el-button>

        <!-- 批量删除 -->
        <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
          @confirm="doBatchDelete">
          <template #reference>
            <el-button type="danger" class="ml10" :disabled="!selectedIds.length">
              批量删除<span v-if="selectedIds.length">（{{ selectedIds.length }}）</span>
            </el-button>
          </template>
        </el-popconfirm>
      </div>

      <div class="right">

        <el-button class="mr8"
          :disabled="!selectedIds.length || !list.some(r => selectedIds.includes(r.id) && r.human_voice_synthetic_url)"
          :loading="zipMaking" @click="downloadBatchZip">
          <el-icon>
            <Download />
          </el-icon>
          批量打包下载ZIP

        </el-button>

        <el-button type="success" round @click="openAdd">
          <el-icon>
            <Plus />
          </el-icon> 添加
        </el-button>
      </div>
    </div>

    <!-- 列表 -->
    <el-table :data="list" border stripe v-loading="loading" style="width: 100%"
      @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="48" />
      <el-table-column prop="id" label="Id" width="80" />
      <el-table-column prop="voice_task_name" label="任务名" min-width="220" show-overflow-tooltip />
      <el-table-column prop="voice_clone_id" label="合成语音（voice_id）" min-width="220" show-overflow-tooltip />
      <el-table-column label="音频" min-width="320">
        <template #default="s">
          <div v-if="s.row.human_voice_synthetic_url" class="audio-cell">
            <audio :src="s.row.human_voice_synthetic_url" controls preload="metadata"></audio>
            <el-button link type="primary" @click="copyText(s.row.human_voice_synthetic_url)">复制</el-button>
          </div>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="s">
          <el-tag :type="s.row.status === 1 ? 'success' : s.row.status === 3 ? 'warning' : 'danger'">
            {{ s.row.status === 1 ? '成功' : s.row.status === 3 ? '处理中' : '失败' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="s">

          <el-button v-if="s.row.human_voice_synthetic_url" type="primary" size="small" @click="downloadOne(s.row)">
            下载
          </el-button>
          <el-button type="danger" size="small" @click="delOne(s.row.id)">
            <el-icon>
              <Delete />
            </el-icon>删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pager">
      <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
        :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
        @current-change="fetchList" />
    </div>

    <!-- 新增弹窗 -->
    <el-dialog v-model="dialogVisible" title="添加" width="720px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="88px">
        <!-- 任务名：支持中/英逗号分割 -->
        <el-form-item label="任务名" prop="voice_task_name">
          <el-input v-model="form.voice_task_name" placeholder="任务名，随便写一个吧">
            <template #append>
              <el-button @click="insertComma('voice_task_name')" text>插入分隔符</el-button>
            </template>
          </el-input>
        </el-form-item>

        <!-- 发音人：多选 -->
        <el-form-item label="发音人" prop="voice_clone_id">
          <el-select v-model="form.voice_clone_id" multiple filterable collapse-tags collapse-tags-tooltip
            placeholder="可多选；默认轮询分配到每行文字" style="width: 100%">
            <el-option v-for="sp in speakers" :key="sp.voice_id" :label="`${sp.voice_name}（${sp.voice_id}）`"
              :value="sp.voice_id" />
          </el-select>
          <div class="muted small" style="margin-top:6px;">
            说明：默认<b>轮询分配</b>；若勾选“全员合成”，则每一行会对所有发音人各合成一次。
          </div>
        </el-form-item>

        <!-- 策略开关：全员合成 -->
        <el-form-item label="策略">
          <el-checkbox v-model="form.apply_to_all">全员合成：每一行文本对所有选中的发音人都合成</el-checkbox>
        </el-form-item>

        <!-- 文本：按换行分割 -->
        <el-form-item label="文字" prop="voice_text">
          <el-input v-model="form.voice_text" type="textarea" :rows="6" placeholder="文字转语音：每一行将单独合成一条" />
          <div class="inline-actions">
            <div class="muted">已输入字数：{{ baseChars }}</div>
            <div class="muted">
              块数（20字/块，向上取整）：
              {{ form.apply_to_all ? `${baseChunks} × ${Math.max(1, form.voice_clone_id.length)} = ${totalChunks}` :
                totalChunks }}
            </div>
            <el-button text @click="insertNewline('voice_text')">插入换行</el-button>
          </div>
          <div class="warn">如需音色用于数字人合成视频上，建议输入不超过500字</div>

          <!-- 预览 -->
          <div v-if="textList.length && form.voice_clone_id.length" class="muted small" style="margin-top:6px;">
            <template v-if="!form.apply_to_all">
              轮询预览（前 5 行）：
              <ul style="margin:6px 0 0 16px;">
                <li v-for="(t, i) in textList.slice(0, 5)" :key="i">
                  第 {{ i + 1 }} 行 → {{ speakerLabelByIndex(i) }}
                </li>
              </ul>
            </template>
            <template v-else>
              全员合成预览（前 3 行 × 所有人）：
              <ul style="margin:6px 0 0 16px;">
                <li v-for="(t, i) in textList.slice(0, 3)" :key="i">
                  第 {{ i + 1 }} 行 → {{speakers.map(s => s.voice_name).join('、')}}
                </li>
              </ul>
            </template>
          </div>
        </el-form-item>

        <!-- 同意协议 -->
        <el-form-item>
          <el-checkbox v-model="agree" :disabled="!agreeEnabled">
            我已阅读并同意
            <el-link type="primary" :underline="false" @click.prevent="openAgreement">《克隆使用协议》</el-link>
          </el-checkbox>
          <span v-if="agreeCountdown > 0" class="muted ml8">（请至少阅读 {{ agreeCountdown }} 秒）</span>
        </el-form-item>

        <!-- 价格信息 -->
        <el-alert :title="priceTip" type="info" :closable="false" show-icon />
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting"
          :disabled="!agreeReady || textList.length === 0 || form.voice_clone_id.length === 0" @click="submit">
          合成语音（预计 {{ totalCost }} 积分 / {{ baseChars }}字）
        </el-button>
      </template>
    </el-dialog>

    <!-- 使用协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" width="720px"
      :close-on-click-modal="false" @closed="stopAgreeCountdown">
      <div v-loading="agreementLoading" style="min-height: 200px;">
        <div v-if="agreementUpdatedAt" class="muted small mb8">最近更新：{{ agreementUpdatedAt }}</div>
        <div class="agreement-content" v-html="agreementContent"></div>
      </div>
      <template #footer>
        <el-button @click="agreementDialogVisible = false">关闭</el-button>
        <el-button type="primary" :disabled="agreeCountdown > 0" @click="acceptAgreement">
          已阅读（{{ agreeCountdown > 0 ? agreeCountdown + 's' : '可确认' }}）
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { saveAs } from 'file-saver'
import { Plus, Refresh, Search, Delete } from '@element-plus/icons-vue'

export default {
  name: 'VoiceRecord',
  components: { Plus, Refresh, Search, Delete },
  data() {
    return {
      loading: false,
      list: [],
      pager: { page: 1, pageSize: 10, total: 0 },
      search: { keyword: '' },

      selectedIds: [],
      speakers: [],

      dialogVisible: false,
      submitting: false,
      zipMaking: false,
      zipProgress: 0,
      // 协议
      agree: false,
      agreeEnabled: false,
      agreeCountdown: 0,
      agreeTimer: null,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',

      // 表单
      form: {
        voice_task_name: '',
        voice_clone_id: [],    // 多选
        voice_text: '',
        apply_to_all: false    // ✅ 新增：全员合成开关
      },
      rules: {
        voice_task_name: [{ required: true, message: '请输入任务名', trigger: 'blur' }],
        voice_clone_id: [{ type: 'array', required: true, message: '请至少选择 1 位发音人', trigger: 'change' }],
        voice_text: [{ required: true, message: '请输入要合成的文字', trigger: 'blur' }]
      },

      unitPer20: 0   // X 积分 / 20字
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
    nameList() { return this.splitByComma(this.form.voice_task_name) },
    textList() { return this.splitByNewline(this.form.voice_text) },

    // 基础字数与块数（不考虑发音人数）
    baseChars() { return this.textList.reduce((n, t) => n + this.charCount(t), 0) },
    baseChunks() { return this.textList.reduce((n, t) => n + this.chunksOfText(t), 0) },

    // 实际块数：全员合成=基础块数×发音人数；否则=基础块数
    totalChunks() {
      const base = this.baseChunks
      return this.form.apply_to_all ? base * Math.max(1, this.form.voice_clone_id.length) : base
    },
    totalCost() { return this.totalChunks * this.unitPer20 },
    priceTip() {
      const p1 = `单价：${this.unitPer20} 积分 / 20字`
      const p2 = this.form.apply_to_all
        ? `；预计：(${this.baseChunks} 块 × 发音人数 ${Math.max(1, this.form.voice_clone_id.length)}) × ${this.unitPer20} = ${this.totalCost} 积分`
        : `；预计：${this.totalChunks} 块 × ${this.unitPer20} = ${this.totalCost} 积分`
      return p1 + p2
    },
    agreeReady() { return this.agree && this.agreeCountdown === 0 }
  },
  mounted() {
    this.fetchList()
    this.fetchSpeakers()
  },
  methods: {
    // ---------- 工具 ----------
    splitByComma(s) { return (s || '').split(/[，,]/g).map(x => x.trim()).filter(Boolean) },
    splitByNewline(s) { return (s || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean) },
    insertComma(field) {
      const v = (this.form[field] || '')
      const ends = /[，,]\s*$/.test(v)
      this.form[field] = ends ? v + ' ' : (v ? v + ', ' : ', ')
    },
    insertNewline(field) {
      const v = (this.form[field] || '')
      this.form[field] = v.endsWith('\n') || v === '' ? v : v + '\n'
    },
    charCount(s) { return (s || '').replace(/\s+/g, '').length },
    chunksOfText(s) { const c = this.charCount(s); return c === 0 ? 0 : Math.ceil(c / 20) },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => this.$message.success('已复制'))
      } else {
        const ta = document.createElement('textarea')
        ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px'
        document.body.appendChild(ta); ta.select()
        try { document.execCommand('copy'); this.$message.success('已复制') }
        catch { this.$message.error('复制失败') }
        finally { document.body.removeChild(ta) }
      }
    },
    speakerLabelByIndex(i) {
      if (!this.speakers?.length || !this.form.voice_clone_id?.length) return '未选择'
      const vid = this.form.voice_clone_id[i % this.form.voice_clone_id.length]
      const found = this.speakers.find(s => s.voice_id === vid)
      return found ? `${found.voice_name}（${found.voice_id}）` : vid
    },
    async downloadBatchZip() {
      const rows = this.list
        .filter(r => this.selectedIds.includes(r.id))
        .filter(r => r.human_voice_synthetic_url)

      if (!rows.length) {
        this.$message.warning('请选择至少一个“已合成”的视频')
        return
      }

      this.zipMaking = true
      try {
        const items = rows.map(r => ({
          url: r.human_voice_synthetic_url,
          filename: this.buildSafeName(`${r.voice_task_name || 'video'}_${r.id}.mp4`)
        }))

        const res = await fetch(`${config.BASE_URL}/shop/api/zip_videos.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        })

        if (!res.ok) throw new Error(`HTTP ${res.status}`)

        const blob = await res.blob()
        saveAs(blob, `videos_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`)
        this.$message.success(`打包完成，共 ${rows.length} 个音频`)
      } catch (e) {
        console.error(e)
        this.$message.error('打包失败，请重试')
      } finally {
        this.zipMaking = false
        this.zipProgress = 0
      }
    },


    // 抓取远端文件为 Blob（注意：若视频URL不允许跨域，将报错）
    async fetchAsBlob(url) {
      // 用 fetch 保留文件名/类型，且能拿到 blob
      const res = await fetch(url, { mode: 'cors' }) // 如跨域受限，可改方案B走后端
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.blob()
    },

    // 简单的文件名清洗
    buildSafeName(name) {
      return String(name)
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, 120) // 防止过长
    },

    // 单个下载（之前的普通下载仍可保留）
    downloadOne(row) {
      if (!row?.human_voice_synthetic_url) {
        this.$message.warning('没有可下载的视频')
        return
      }
      this.forceDownload(row.human_voice_synthetic_url, `${row.voice_task_name || 'video'}_${row.id}.mp4`)
    },

    forceDownload(url, filename) {
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'video.mp4'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    // ---------- 列表 ----------
    async fetchList() {
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: this.search.keyword ? 'search' : 'list',
          store_id: this.userInfo.id,
          keyword: this.search.keyword,
          page: this.pager.page,
          page_size: this.pager.pageSize
        })
        this.list = data.data || []
        this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
        if (!this.list.length && this.pager.page > 1) {
          this.pager.page = this.pager.page - 1
          await this.fetchList()
        }
        this.selectedIds = []
      } catch (e) {
        this.$message.error('获取列表失败')
      } finally {
        this.loading = false
      }
    },
    doSearch() { this.pager.page = 1; this.fetchList() },
    handleSelectionChange(rows) { this.selectedIds = rows.map(r => r.id) },
    async doBatchDelete() {
      if (!this.selectedIds.length) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: 'batch_delete', store_id: this.userInfo.id, ids: this.selectedIds
        })
        if (data.success) { this.$message.success('批量删除成功'); await this.fetchList() }
        else { this.$message.error(data.error || '批量删除失败') }
      } catch { this.$message.error('批量删除失败') }
      finally { this.loading = false }
    },
    async delOne(id) {
      try {
        await this.$confirm('确定删除该记录吗？', '提示', { type: 'warning' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: 'delete', id, store_id: this.userInfo.id
        })
        if (data.success) { this.$message.success('已删除'); this.fetchList() }
        else { this.$message.error(data.error || '删除失败') }
      } catch (e) { console.info('删除取消或出错：', e) }
    },

    // ---------- 发音人 ----------
    async fetchSpeakers() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone.php`, {
          action: 'list_all', store_id: this.userInfo.id
        })
        const rows = data?.data || []
        this.speakers = rows
          .filter(r => r.voice_id)
          .map(r => ({ voice_id: r.voice_id, voice_name: r.voice_name || r.voice_id }))
      } catch (e) { this.speakers = [] }
    },

    // ---------- 弹窗 ----------
    async openAdd() {
      this.dialogVisible = true
      // 协议复位
      this.agree = false; this.agreeEnabled = false; this.stopAgreeCountdown()
      // 表单复位
      this.form = { voice_task_name: '', voice_clone_id: [], voice_text: '', apply_to_all: false }
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())

      // 单价（X 分 / 20字）
      try {
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        const v = Number(parseFloat(data?.data?.digital_human_voice_synth_num || 0).toFixed(2))
        this.unitPer20 = isNaN(v) ? 0 : v
      } catch { this.unitPer20 = 0 }
    },

    // ---------- 协议 ----------
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch { this.$message.error('读取使用协议失败'); this.agreementContent = '' }
      finally { this.agreementLoading = false; this.startAgreeCountdown(3) }
    },
    startAgreeCountdown(sec) {
      this.stopAgreeCountdown()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) this.agreeCountdown--
        if (this.agreeCountdown <= 0) this.stopAgreeCountdown()
      }, 1000)
    },
    stopAgreeCountdown() { if (this.agreeTimer) clearInterval(this.agreeTimer); this.agreeTimer = null },
    acceptAgreement() {
      if (this.agreeCountdown > 0) return
      this.agreementDialogVisible = false
      this.agreeEnabled = true
      this.agree = true
    },

    // ---------- 提交 ----------
    async submit() {
      this.$refs.formRef.validate(async (ok) => {
        if (!ok) return
        if (!this.agreeReady) { this.$message.warning('请先阅读并同意克隆使用协议'); return }
        if (this.form.voice_clone_id.length === 0) { this.$message.warning('请选择发音人'); return }
        if (this.textList.length === 0) { this.$message.warning('请至少输入一行文字'); return }

        this.submitting = true
        try {
          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            // 任务名：数组或字符串都支持
            voice_task_name: this.nameList.length > 1 ? this.nameList : (this.nameList[0] || ''),
            // 发音人：数组或字符串
            voice_clone_id: this.form.voice_clone_id.length > 1 ? this.form.voice_clone_id : (this.form.voice_clone_id[0] || ''),
            // 文本：按行拆分，数组或字符串
            voice_text: this.textList.length > 1 ? this.textList : (this.textList[0] || ''),
            // ✅ 策略
            apply_to_all: this.form.apply_to_all
          }
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, payload)
          if (data.success) {
            this.$message.success('已提交合成任务')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(data.error || '提交失败')
          }
        } catch { this.$message.error('提交失败') }
        finally { this.submitting = false }
      })
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 12px 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.ml10 {
  margin-left: 10px;
}

.ml8 {
  margin-left: 8px;
}

.muted {
  color: #9ca3af;
}

.small {
  font-size: 12px
}

.mb8 {
  margin-bottom: 8px
}

.pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.inline-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}

.warn {
  color: #f59e0b;
  font-size: 12px;
  margin-top: 6px;
}

.audio-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audio-cell audio {
  width: 240px;
  max-width: 100%;
}

.agreement-content {
  max-height: 60vh;
  overflow-y: auto;
  white-space: pre-wrap;
  line-height: 1.7;
}
</style>
